import React, { 
    createContext,
    useContext,
    ReactNode,
    useMemo,
} from 'react';

export interface MessageContextValue {
    selectedOption?: string;
    sendMessage: (text: string) => Promise<void>;
    isActionable?: boolean;
}

const MessageContext = createContext<MessageContextValue | undefined>(undefined);

export interface MessageProviderProps {
    children: ReactNode;
    selectedOption?: string;
    sendMessage: (text: string) => Promise<void>;
    isActionable?: boolean;
}

export const MessageProvider: React.FC<MessageProviderProps> = ({
    children,
    selectedOption,
    sendMessage,
    isActionable,
}) => {
    // Memoize the context value to prevent unnecessary re-renders
    // Only create a new object when the actual values change
    const value: MessageContextValue = useMemo(() => ({
        selectedOption,
        sendMessage,
        isActionable,
    }), [selectedOption, sendMessage, isActionable]);

    return (
        <MessageContext.Provider value={value}>
            {children}
        </MessageContext.Provider>
    );
};

export const useMessageContext = (): MessageContextValue => {
    const context = useContext(MessageContext);
    if (context === undefined) {
        throw new Error('useMessageContext must be used within a MessageProvider');
    }
    return context;
};
